// SOVRANOVA — always-present, ZERO-NETWORK crisis lifeline.
// Mounted at the root (in main.jsx, OUTSIDE the app's ErrorBoundary) so it stays
// reachable on every screen — loading, auth, onboarding, dashboard, every voice
// surface — and even if the app itself crashes to its error fallback. It makes
// NO network calls: the numbers are hardcoded tel: links, so a person is one tap
// from real help even fully offline. Calm by default (a discreet pill); one tap
// reveals the call links. Trauma-informed: present and reassuring, never alarming.
import { useState } from 'react'
import { resolveRegion, getCrisisResources, HELPLINE_DIRECTORY_URL } from '../services/crisisResources'

export function CrisisLifeline() {
  const [open, setOpen] = useState(false)
  // Region resolved locally (offline-safe); recomputed each render so it reflects
  // the user's profile region once App.jsx stores it after sign-in.
  const res = getCrisisResources(resolveRegion())

  return (
    <div style={S.wrap} role="complementary" aria-label="Urgent support">
      {open && (
        <div style={S.panel}>
          <p style={S.lead}>You're not alone. Support is available right now.</p>
          {/* GLOBAL / EU carry no tel: number. Never render a dead `tel:null` link
              and never substitute another country's number: link the worldwide
              directory and name the person's own local emergency number. */}
          {res.primary.tel
            ? <a href={`tel:${res.primary.tel}`} style={S.callPrimary} aria-label={`Call ${res.primary.label} on ${res.primary.number}`}>
                <span style={S.callLabel}>{res.primary.label}</span>
                <span style={S.callNumber}>{res.primary.number}</span>
              </a>
            : <a href={HELPLINE_DIRECTORY_URL} target="_blank" rel="noopener noreferrer" style={S.callPrimary} aria-label={`Open ${res.primary.label} at ${res.primary.number}`}>
                <span style={S.callLabel}>{res.primary.label}</span>
                <span style={S.callNumber}>{res.primary.number}</span>
              </a>}
          {res.emergency.tel
            ? <a href={`tel:${res.emergency.tel}`} style={S.callSecondary} aria-label={`Call emergency services on ${res.emergency.number}`}>
                <span style={S.callLabel}>{res.emergency.label}</span>
                <span style={S.callNumber}>{res.emergency.number}</span>
              </a>
            : <div style={S.callSecondary} role="note" aria-label={`Call ${res.emergency.number}`}>
                <span style={S.callLabel}>{res.emergency.label}</span>
                <span style={{ ...S.callNumber, fontSize: '0.85rem' }}>{res.emergency.number}</span>
              </div>}
          <p style={S.region}>{res.region} · 24/7</p>
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={open ? S.tabOpen : S.tab}
        aria-expanded={open}
        aria-label={open ? 'Close urgent support' : 'Open urgent support'}
      >
        <span aria-hidden="true" style={S.heart}>♥</span>
        <span>{open ? 'Close' : 'Urgent help'}</span>
      </button>
    </div>
  )
}

const tabBase = {
  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
  padding: '0.5rem 0.85rem', borderRadius: '999px',
  background: 'rgba(28,18,46,0.92)', color: '#f3ede1',
  boxShadow: '0 4px 16px rgba(0,0,0,0.28)',
  fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
  WebkitBackdropFilter: 'blur(6px)', backdropFilter: 'blur(6px)',
}

const callBase = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  textDecoration: 'none', padding: '0.6rem 0.75rem', borderRadius: '0.7rem',
}

const S = {
  wrap: {
    position: 'fixed',
    right: 'max(16px, env(safe-area-inset-right))',
    bottom: 'max(16px, env(safe-area-inset-bottom))',
    zIndex: 2147483000,
    display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.5rem',
  },
  tab:     { ...tabBase, border: '1px solid rgba(201,162,39,0.5)' },
  tabOpen: { ...tabBase, border: '1px solid rgba(201,162,39,0.78)', background: 'rgba(36,24,58,0.96)' },
  heart:   { color: '#e6a6b0', fontSize: '0.9rem', lineHeight: 1 },
  panel: {
    width: 232, maxWidth: '80vw',
    background: 'rgba(20,12,34,0.96)', border: '1px solid rgba(201,162,39,0.45)',
    borderRadius: '0.9rem', padding: '0.85rem 0.9rem',
    boxShadow: '0 12px 36px rgba(0,0,0,0.42)', color: '#f3ede1',
    display: 'flex', flexDirection: 'column', gap: '0.55rem',
    WebkitBackdropFilter: 'blur(8px)', backdropFilter: 'blur(8px)',
  },
  lead:   { margin: 0, fontSize: '0.85rem', lineHeight: 1.5 },
  callPrimary:   { ...callBase, background: 'rgba(201,162,39,0.16)', border: '1px solid rgba(201,162,39,0.55)', color: '#f7eccb' },
  callSecondary: { ...callBase, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.18)', color: '#f3ede1' },
  callLabel:  { fontSize: '0.8rem', fontWeight: 600, opacity: 0.95 },
  callNumber: { fontSize: '0.98rem', fontWeight: 800, letterSpacing: '0.02em' },
  region: { margin: 0, fontSize: '0.68rem', color: 'rgba(243,237,225,0.55)', textAlign: 'center' },
}
